import { ReferenceRangeDisplay } from "@/components/ReferenceRangeDisplay/ReferenceRangeDisplay";
import { Button, Tooltip, toast } from "@/components/ui";
import { Badge } from "@/components/ui/Badge";
import { BodyText } from "@/components/ui/Text";
import {
  type QuestionnaireHistoryItem,
  useQuestionnaireHistory,
} from "@/features/Patient/MedicalRecords/HealthMaintenance/components/QuestionnaireHistoryModal/useQuestionnaireHistory";
import {
  HistoryModal,
  type HistoryRow,
} from "@/features/Patient/MedicalRecords/components/HistoryModal";
import { formatDate, formatDateTime } from "@/lib/time";
import type {
  LifestyleQuestionnaireTypes,
  MentalHealthQuestionnaireTypes,
} from "@/types/questionnaires";
import * as React from "react";
import {
  QuestionnaireActionType,
  useQuestionnaireAvailableActions,
  useVoidQuestionnaire,
} from "../../network";
import { formatQuestionnaireScore } from "../../utils";

interface QuestionnaireHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  questionnaireType:
    | LifestyleQuestionnaireTypes
    | MentalHealthQuestionnaireTypes;
  questionnaireDisplayName: string;
  onSelectResponse?: (responseId: string) => void;
}

function getResultTag(questionnaireHistoryItem: QuestionnaireHistoryItem) {
  if (!questionnaireHistoryItem.confirmationMetadata) {
    return (
      <Badge variant="warning" className="w-fit">
        <BodyText textColor="warning" size="sm">
          Not Reviewed
        </BodyText>
      </Badge>
    );
  }
}

function getInfoItems(questionnaireHistoryItem: QuestionnaireHistoryItem) {
  const items = [];
  const performedByPatient =
    questionnaireHistoryItem.author?.type === "Patient";

  if (questionnaireHistoryItem.confirmationMetadata) {
    const actor = questionnaireHistoryItem.confirmationMetadata.actor?.display;
    items.push(
      `Reviewed by ${actor} on ${formatDateTime(questionnaireHistoryItem.confirmationMetadata.occurred)}`,
    );
  }
  items.push(
    `Performed by ${questionnaireHistoryItem.author?.display} ${performedByPatient ? "(Patient)" : ""} on ${formatDateTime(questionnaireHistoryItem.authored)}`,
  );
  return items;
}

function renderScore(
  questionnaireHistoryItem: QuestionnaireHistoryItem,
  resultTag?: React.ReactNode,
  onSelectResponse?: (responseId: string) => void,
): React.ReactNode {
  const score = formatQuestionnaireScore(
    questionnaireHistoryItem.responseScore,
    questionnaireHistoryItem.maxScore,
    "--",
  );
  const { criticality, referenceRanges } = questionnaireHistoryItem;

  const content = (
    <div className="flex items-center gap-2">
      <ReferenceRangeDisplay
        criticality={criticality ?? null}
        data={score}
        dataClassName={
          onSelectResponse
            ? "underline decoration-1 underline-offset-2"
            : "tracking-wider"
        }
        referenceRanges={referenceRanges ?? null}
        tooltipFallback={onSelectResponse ? "See Results" : ""}
        tooltipContentClassName="z-30"
      />
      {!onSelectResponse && resultTag}
    </div>
  );

  if (onSelectResponse) {
    return (
      <div className="flex items-center gap-2">
        <Tooltip tooltipContentText={undefined} side="bottom">
          <Button
            variant="link"
            size="sm"
            className="font-normal p-0 h-auto"
            onClick={() =>
              onSelectResponse(questionnaireHistoryItem.questionnaireResponseId)
            }
          >
            {content}
          </Button>
        </Tooltip>
        {resultTag}
      </div>
    );
  }

  return content;
}

export const QuestionnaireHistoryModal = ({
  open,
  onOpenChange,
  patientId,
  questionnaireType,
  questionnaireDisplayName,
  onSelectResponse,
}: QuestionnaireHistoryModalProps) => {
  const { data, isLoading, isError } = useQuestionnaireHistory({
    patientId,
    questionnaireType,
    enabled: open,
  });

  const { data: availableActions, isError: isAvailableActionsError } =
    useQuestionnaireAvailableActions(patientId, questionnaireType);

  const { mutate: voidQuestionnaire, isPending: isVoidingQuestionnaire } =
    useVoidQuestionnaire({
      patientId,
      questionnaireType,
      onSuccess: () => {
        toast.success("Questionnaire voided successfully");
      },
    });

  const voidAction =
    availableActions?.[QuestionnaireActionType.VOID_QUESTIONNAIRE];
  const isVoidDisabled =
    !isAvailableActionsError && Boolean(voidAction?.unavailableReason);

  const rows: HistoryRow[] = React.useMemo(() => {
    const items = data?.questionnaireHistory ?? [];
    return items.map((item) => {
      const resultTag = getResultTag(item);
      return {
        id: item.questionnaireResponseId,
        date: formatDate(item.authored),
        result: renderScore(item, resultTag, onSelectResponse),
        resultTag: undefined,
        infoItems: getInfoItems(item),
        // Only show void button if action is available
        onVoid: isVoidDisabled
          ? undefined
          : () =>
              voidQuestionnaire({
                questionnaireResponseId: item.questionnaireResponseId,
              }),
        isVoidPending: isVoidingQuestionnaire,
      };
    });
  }, [
    data,
    voidQuestionnaire,
    isVoidingQuestionnaire,
    isVoidDisabled,
    onSelectResponse,
  ]);

  return (
    <HistoryModal
      emptyMessage="No questionnaire history found."
      title={questionnaireDisplayName}
      rows={rows}
      onOpenChange={onOpenChange}
      open={open}
      isLoading={isLoading}
      isError={isError}
    />
  );
};
